/**
 * Employee API Service
 * Handles all employee-related API calls
 */

import type {
    Employee,
    Attendance,
    DailyAttendanceSummary,
    LeaveRequest,
    Payroll,
    LeaveBalance,
    EmployeeLoginRequest,
    EmployeeLoginResponse,
    AttendanceCheckInRequest,
    LeaveRequestCreate,
    ApiResponse
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://securitybackend.magnytesolution.com/api';
// const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://nfc.arbotix.online/api';

// Helper to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('employee_access_token') || localStorage.getItem('auth_token');
};

// Helper to set auth headers
const getAuthHeaders = (): HeadersInit => {
    const token = getAuthToken();
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

// ==================== Employee Authentication ====================

export const employeeApi = {
    // Login
    login: async (credentials: EmployeeLoginRequest): Promise<ApiResponse<EmployeeLoginResponse>> => {
        const response = await fetch(`${API_BASE_URL}/employee/login/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials)
        });

        const data = await response.json();

        if (data.status && data.data) {
            // Store tokens
            localStorage.setItem('employee_access_token', data.data.access_token);
            localStorage.setItem('employee_refresh_token', data.data.refresh_token);
            localStorage.setItem('employee_data', JSON.stringify(data.data.employee));
        }

        return data;
    },

    // Logout
    logout: async (): Promise<ApiResponse<null>> => {
        const response = await fetch(`${API_BASE_URL}/employee/logout/`, {
            method: 'POST',
            headers: getAuthHeaders()
        });

        const data = await response.json();

        // Clear local storage
        localStorage.removeItem('employee_access_token');
        localStorage.removeItem('employee_refresh_token');
        localStorage.removeItem('employee_data');

        return data;
    },

    // Get profile
    getProfile: async (): Promise<ApiResponse<Employee>> => {
        const response = await fetch(`${API_BASE_URL}/employee/profile/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Update profile
    updateProfile: async (data: Partial<Employee>): Promise<ApiResponse<Employee>> => {
        const response = await fetch(`${API_BASE_URL}/employee/profile/`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },

    // Upload document
    uploadDocument: async (documentType: string, documentUrl: string, documentName?: string): Promise<ApiResponse<Employee>> => {
        const response = await fetch(`${API_BASE_URL}/employee/upload-document/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                document_type: documentType,
                document_url: documentUrl,
                document_name: documentName
            })
        });
        return response.json();
    },

    // Get stored employee data
    getStoredEmployee: (): Employee | null => {
        const data = localStorage.getItem('employee_data');
        return data ? JSON.parse(data) : null;
    },

    // Check if logged in
    isLoggedIn: (): boolean => {
        return !!getAuthToken();
    }
};

// ==================== Attendance Management ====================

export const attendanceApi = {
    // Check in
    checkIn: async (location: AttendanceCheckInRequest): Promise<ApiResponse<Attendance>> => {
        const response = await fetch(`${API_BASE_URL}/attendance/check-in/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(location)
        });
        return response.json();
    },

    // Check out
    checkOut: async (location: AttendanceCheckInRequest): Promise<ApiResponse<Attendance>> => {
        const response = await fetch(`${API_BASE_URL}/attendance/check-out/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(location)
        });
        return response.json();
    },

    // Get today's attendance
    getToday: async (): Promise<ApiResponse<DailyAttendanceSummary | null>> => {
        const response = await fetch(`${API_BASE_URL}/attendance/today/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Get attendance history
    getHistory: async (month?: number, year?: number): Promise<ApiResponse<Attendance[]>> => {
        const params = new URLSearchParams();
        if (month) params.append('month', month.toString());
        if (year) params.append('year', year.toString());

        const response = await fetch(`${API_BASE_URL}/attendance/my-attendance/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    }
};

// ==================== Leave Management ====================

export const leaveApi = {
    // Create leave request
    create: async (leaveData: LeaveRequestCreate): Promise<ApiResponse<LeaveRequest>> => {
        const response = await fetch(`${API_BASE_URL}/leave/request/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(leaveData)
        });
        return response.json();
    },

    // Get my leaves
    getMyLeaves: async (): Promise<ApiResponse<LeaveRequest[]>> => {
        const response = await fetch(`${API_BASE_URL}/leave/my-leaves/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Get leave balance
    getBalance: async (): Promise<ApiResponse<LeaveBalance>> => {
        const response = await fetch(`${API_BASE_URL}/leave/balance/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    }
};

// ==================== Payroll Management ====================

export const payrollApi = {
    // Get salary history
    getMySalary: async (): Promise<ApiResponse<Payroll[]>> => {
        const response = await fetch(`${API_BASE_URL}/payroll/my-salary/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Get current month salary
    getCurrentMonth: async (): Promise<ApiResponse<any>> => {
        const response = await fetch(`${API_BASE_URL}/payroll/current-month/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    }
};

// ==================== Admin APIs ====================

export const adminEmployeeApi = {
    // Get all employees
    getAll: async (filters?: { search?: string; is_active?: boolean; department?: string }): Promise<ApiResponse<Employee[]>> => {
        const params = new URLSearchParams();
        if (filters?.search) params.append('search', filters.search);
        if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
        if (filters?.department) params.append('department', filters.department);

        const response = await fetch(`${API_BASE_URL}/admin/employees/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Create employee
    create: async (employeeData: Partial<Employee>): Promise<ApiResponse<Employee>> => {
        const response = await fetch(`${API_BASE_URL}/admin/employees/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(employeeData)
        });
        return response.json();
    },

    // Get employee by ID
    getById: async (id: number): Promise<ApiResponse<Employee>> => {
        const response = await fetch(`${API_BASE_URL}/admin/employees/${id}/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Update employee
    update: async (id: number, data: Partial<Employee>): Promise<ApiResponse<Employee>> => {
        const response = await fetch(`${API_BASE_URL}/admin/employees/${id}/`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },

    // Delete (deactivate) employee
    delete: async (id: number): Promise<ApiResponse<null>> => {
        const response = await fetch(`${API_BASE_URL}/admin/employees/${id}/`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return response.json();
    }
};

export const adminAttendanceApi = {
    // Get all attendance
    getAll: async (filters?: { employee_id?: number; date_from?: string; date_to?: string; status?: string }): Promise<ApiResponse<Attendance[]>> => {
        const params = new URLSearchParams();
        if (filters?.employee_id) params.append('employee_id', filters.employee_id.toString());
        if (filters?.date_from) params.append('date_from', filters.date_from);
        if (filters?.date_to) params.append('date_to', filters.date_to);
        if (filters?.status) params.append('status', filters.status);

        const response = await fetch(`${API_BASE_URL}/admin/attendance/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Get employee attendance
    getEmployeeAttendance: async (employeeId: number, month?: number, year?: number): Promise<ApiResponse<any>> => {
        const params = new URLSearchParams();
        if (month) params.append('month', month.toString());
        if (year) params.append('year', year.toString());

        const response = await fetch(`${API_BASE_URL}/admin/attendance/employee/${employeeId}/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Mark attendance
    markAttendance: async (data: { employee_id: number; date: string; status: string; notes?: string }): Promise<ApiResponse<Attendance>> => {
        const response = await fetch(`${API_BASE_URL}/admin/attendance/mark/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },

    // Get attendance report
    getReport: async (month?: number, year?: number): Promise<ApiResponse<any>> => {
        const params = new URLSearchParams();
        if (month) params.append('month', month.toString());
        if (year) params.append('year', year.toString());

        const response = await fetch(`${API_BASE_URL}/admin/attendance/report/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    }
};

export const adminLeaveApi = {
    // Get all leaves
    getAll: async (filters?: { status?: string; employee_id?: number }): Promise<ApiResponse<LeaveRequest[]>> => {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.employee_id) params.append('employee_id', filters.employee_id.toString());

        const response = await fetch(`${API_BASE_URL}/admin/leaves/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Get pending leaves
    getPending: async (): Promise<ApiResponse<LeaveRequest[]>> => {
        const response = await fetch(`${API_BASE_URL}/admin/leaves/pending/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Approve leave
    approve: async (id: number, remarks?: string): Promise<ApiResponse<LeaveRequest>> => {
        const response = await fetch(`${API_BASE_URL}/admin/leaves/${id}/approve/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ admin_remarks: remarks })
        });
        return response.json();
    },

    // Reject leave
    reject: async (id: number, remarks?: string): Promise<ApiResponse<LeaveRequest>> => {
        const response = await fetch(`${API_BASE_URL}/admin/leaves/${id}/reject/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ admin_remarks: remarks })
        });
        return response.json();
    }
};

export const adminPayrollApi = {
    // Get all payroll
    getAll: async (filters?: { employee_id?: number; month?: number; year?: number; payment_status?: string }): Promise<ApiResponse<Payroll[]>> => {
        const params = new URLSearchParams();
        if (filters?.employee_id) params.append('employee_id', filters.employee_id.toString());
        if (filters?.month) params.append('month', filters.month.toString());
        if (filters?.year) params.append('year', filters.year.toString());
        if (filters?.payment_status) params.append('payment_status', filters.payment_status);

        const response = await fetch(`${API_BASE_URL}/admin/payroll/?${params}`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Generate payroll
    generate: async (data: { employee_id?: number; month: number; year: number }): Promise<ApiResponse<Payroll[]>> => {
        const response = await fetch(`${API_BASE_URL}/admin/payroll/generate/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },

    // Get employee payroll
    getEmployeePayroll: async (employeeId: number): Promise<ApiResponse<Payroll[]>> => {
        const response = await fetch(`${API_BASE_URL}/admin/payroll/employee/${employeeId}/`, {
            headers: getAuthHeaders()
        });
        return response.json();
    },

    // Mark as paid
    markPaid: async (id: number): Promise<ApiResponse<Payroll>> => {
        const response = await fetch(`${API_BASE_URL}/admin/payroll/${id}/mark-paid/`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return response.json();
    }
};
